import {Component, OnDestroy, OnInit} from '@angular/core';
import {BsModalService} from "ngx-bootstrap/modal";
import {LoggerService} from "../../common/logger.service";
import {CartService} from "../cart.service";
import {SessionService} from "../../common/session.service";
import {AoCartService} from "../../ao-cart/ao-cart.service";
import {ShippingService} from "../../shipping/shipping.service";
import {ConfirmDialogService} from "../../common/confirm-dialog/confirm-dialog.service";
import {AlertUtilService} from "../../alert/alert-util.service";
import {TranslateService} from "../../translation/translate.service";
import {AnalyticsService} from "../../analytics/analytics.service";
import {AoCartOpenerService} from "../../ao-cart/ao-cart-opener.service";
import {BaseComponent} from "../../base.component";
import {from, noop, of, Subscription} from "rxjs";
import {ICart, IFreeShippingMessage} from "../cart-models";
import {Constants} from "../../constants";
import {uxCartRouterCommands} from "../../util/locale-util";
import {AutoOrder} from "../../autoorder/auto-order-models";
import * as _ from 'lodash';
import {CartSavedProductService} from "../cart-saved-product/cart-saved-product.service";
import {ISavedProduct} from "../cart-saved-product/cart-saved-product-models";
import {ActivatedRoute, Router} from "@angular/router";
import {UserService} from "../../common/user.service";
import {find, map, switchMap, take} from "rxjs/operators";
import {UsanaCurrencyPipe} from "../../currency/usana-currency.pipe";
import {IPaymentMethod} from "../../../checkout/models/payment/payment-method";
import {GqlPaymentService} from "../../../checkout/payment/gql-payment.service";
import {ProductCreditUtil} from "../../util/product-credit-util";
import {MarkupService} from "../../common/markup.service";
import {CartUtil} from "../cart-util";
import {GqlShareListService} from "../../social/share-list/gql-share-list.service";
import {ShareListService} from "../../social/share-list.service";
import {IShareList, IShareListProduct, ISponsorDetails} from "../../social/social-models";
import {ShareListShareModalComponent} from "../../social/share-list-share-modal/share-list-share-modal.component";
import {IProductLine} from "../cart-models";
import {UsanaDatePipe} from "../../common/usana-date.pipe";
import {SponsorService} from "../../common/sponsor.service";
import {ShareableUtil} from "../../social/shareable-util";
import {CustomerUtil} from "../../util/customer-util";

@Component({
    selector: 'cart-overview',
    templateUrl: './cart-overview.component.html',
    styleUrls: ['./cart-overview.component.scss'],
    standalone: false
})
export class CartOverviewComponent extends BaseComponent implements OnInit, OnDestroy {

    showRenewalFeeMessage: boolean = false;
    hideWillCall: boolean = false;
    hideFreeShippingMessage: boolean = false;
    productCreditBalanceText: string;
    hideProductCreditBalanceText: boolean = false;

    cart: ICart;
    cartBusy: boolean = false;
    checkoutButtonClicked: boolean = false;
    saveAsShareListBusy: boolean = false;
    showShareListArrivalBanner: boolean = false;
    shareListArrivalSponsorName: string;
    shareListArrivalExistingMoved: boolean = false;
    freeShippingMessage: IFreeShippingMessage;
    cartSubscriptions: Subscription;
    saveForLaterProducts: ISavedProduct[];

    constructor(loggerService: LoggerService,
                userService: UserService,
                private cartService: CartService,
                private sessionService: SessionService,
                private aoCartService: AoCartService,
                private shippingService: ShippingService,
                private confirmDialogService: ConfirmDialogService,
                private alertUtilService: AlertUtilService,
                private translateService: TranslateService,
                private router: Router,
                private usanaCurrencyPipe: UsanaCurrencyPipe,
                private analyticsService: AnalyticsService,
                private modalService: BsModalService,
                private aoCartOpenerService: AoCartOpenerService,
                private savedProductService: CartSavedProductService,
                private markupService: MarkupService,
                private gqlPaymentService: GqlPaymentService,
                private gqlShareListService: GqlShareListService,
                private shareListService: ShareListService,
                private usanaDatePipe: UsanaDatePipe,
                private sponsorService: SponsorService,
                private route: ActivatedRoute) {
        super(userService, loggerService);
    }

    ngOnInit() {
        this.alertUtilService.closeAllAlerts();
        this.cartSubscriptions = this.cartService.cart$.subscribe(cart => {
            if (!_.isEmpty(cart)) {
                this.cart = cart;
                this.getProductCreditsDisplay();
                this.updateFreeShippingMessage();
                this.setShowRenewalFeeMessage();
                this.analyticsService.viewCartEvent(this.cart);
            }
        });

        const shareListArrival = this.route.snapshot.queryParamMap.get('shareListArrival');
        if (shareListArrival === 'true') {
            this.shareListArrivalExistingMoved = this.route.snapshot.queryParamMap.get('existingProductsMoved') === 'true';
            this.cartSubscriptions.add(
                this.sponsorService.sponsorDetailsSubject.watch().pipe(
                    find((sponsorDetails: ISponsorDetails) => ShareableUtil.isSponsorDetailsPresent(sponsorDetails)),
                    map((sponsorDetails: ISponsorDetails) => {
                        this.shareListArrivalSponsorName = sponsorDetails?.pcReferralOtherName
                            || sponsorDetails?.pcReferralName
                            || CustomerUtil.getSponsorDisplayName(sponsorDetails);
                        this.showShareListArrivalBanner = true;
                    })
                ).subscribe()
            );
        }

        this.cartSubscriptions.add(this.savedProductService.getSavedForLaterProducts().subscribe());
        this.cartSubscriptions.add(this.savedProductService.savedProducts$.subscribe((products: ISavedProduct[]) => this.saveForLaterProducts = products));
        this.cartSubscriptions.add(this.cartService.cartBusy$.subscribe((cartBusy: boolean) => this.cartBusy = cartBusy));
        this.markupService.setTitleFromTranslationTag("cart.cart.overview.page.title");
    }

    ngOnDestroy(): void {
        this.cartSubscriptions.unsubscribe();
    }

    get checkoutAvailable(): boolean {
        return !!this.cart && !!this.cart.products && this.cart.products.length > 0 && !this.checkoutButtonClicked && !this.cartBusy;
    }

    get showWillCallOnlyMessage(): boolean {
        return this.cart?.willCallOnly && this.multiWarehouse?.enabled && this.multiWarehouse?.productBased && !this.hideWillCall && !this.eventShopping;
    }

    updateFreeShippingMessage() {
        this.shippingService.getFreeShippingMessage(this.cart.freeShippingEligible, this.cart.freeShippingThresholdRemaining)
            .pipe(take(1))
            .subscribe((freeShippingMessage) => this.freeShippingMessage = freeShippingMessage);
    }

    getProductCreditsDisplay() {
        this.gqlPaymentService.loadProductCredits().pipe(
            switchMap((productCredits: IPaymentMethod[]) => {
                if (productCredits?.length > 0) {
                    return this.usanaCurrencyPipe.transform(ProductCreditUtil.sum(productCredits)).pipe(take(1),
                        switchMap((formattedBalance: any) => {
                            return from(this.translateService.translateRequest({
                                tag: 'cart.product.credit.balance',
                                replacements: new Map([['amount', formattedBalance]])
                            })).pipe(map((translatedValue: string) => translatedValue));
                        })
                    );
                }
                return of(null);
            })
        ).subscribe((translatedValue: string) => {
            this.productCreditBalanceText = translatedValue;
        });
    }

    private goToCheckout() {
        this.sessionService.isPcUpgrade().then((isPcUpgrade: boolean) => {
            if (isPcUpgrade && this.allowPcSignup) {
                this.checkoutButtonClicked = false;
                this.router.navigate(uxCartRouterCommands(Constants.ENROLL_STATE, Constants.ENROLL_PC_STATE));
            } else {
                this.cartService.checkout()
                    .catch(noop)
                    .finally(() => this.checkoutButtonClicked = false);
            }
        });
    }

    checkout(): void {
        if (this.checkoutAvailable) {
            this.checkoutButtonClicked = true;
            let hasUnsavedCarts = false;
            this.alertUtilService.closeAllAlerts();
            _.forEach(this.aoCartService.autoOrders, (autoOrder: AutoOrder) => {
                if (this.aoCartService.hasAoChanged(autoOrder)) {
                    hasUnsavedCarts = true;
                    this.confirmDialogService.open("md", {title: "cart.ao.check.title"}, [{
                        body: "cart.ao.check.body.text",
                        translate: true
                    }], {
                        name: "cart.ao.check.open.ao",
                        confirmAction: () => this.aoCartOpenerService.openAoModal(autoOrder.orderId)
                    }, {
                        name: "cart.ao.check.continue.checkout",
                        cancelAction: () => {
                            this.aoCartService.cancelAoChanges(autoOrder);
                            this.goToCheckout();
                        },
                        show: true
                    });
                    return;
                }
            });

            if (hasUnsavedCarts) {
                this.checkoutButtonClicked = false;
            } else {
                this.goToCheckout();
            }
        }
    }

    get showSaveAsShareList(): boolean {
        return this.authenticated && !!this.cart?.products?.length;
    }

    saveCartAsShareList(): void {
        if (this.saveAsShareListBusy) return;
        this.saveAsShareListBusy = true;

        const shareListProducts: IShareListProduct[] = this.cart.products.map((line: IProductLine, index: number) => ({
            usanaId: line.product.usanaId,
            quantity: line.quantity,
            productOrder: index
        }));

        from(this.translateService.translate("cart.share.list.default.name")).pipe(
            switchMap((translatedListName: string) =>
                this.usanaDatePipe.transform(new Date(), 'shortDate').pipe(
                    take(1),
                    map((formattedDate: string) =>
                        this.shareListService.getDefaultCartShareListName(translatedListName, formattedDate)
                    )
                )
            )
        ).subscribe((defaultListName: string) => {
            // Combined flow: create the list up front with the default name, then open
            // the share modal in "manage" mode (inline-editable title + Cancel/Done).
            const shareList: IShareList = {listName: defaultListName, shareListProducts, listType: Constants.LIST_TYPE_SHARE_CART};
            this.gqlShareListService.createShareList(shareList).pipe(take(1)).subscribe({
                next: (created: IShareList) => {
                    this.saveAsShareListBusy = false;
                    this.modalService.show(ShareListShareModalComponent, {
                        initialState: {
                            shareList: created,
                            manageMode: true,
                            shareOptions: {
                                shareType: Constants.SHARE_TYPE_SHARE_LIST,
                                shareId: created.shareListId.toString(),
                                shareFrom: Constants.SHARE_LIST_STATE
                            }
                        },
                        class: 'modal-md'
                    });
                },
                error: () => {
                    this.saveAsShareListBusy = false;
                    this.alertUtilService.addWarning({body: 'cart.share.list.create.error', translate: true});
                }
            });
        });
    }

    setShowRenewalFeeMessage() {
        this.showRenewalFeeMessage = this.expiredCustomer && !!_.find(this.cart.products, (cartProduct) => cartProduct.product.usanaId === Constants.RENEWAL_FEE_820_PRODUCT_CODE);
    }

    getItemsTotalQuantityTag(qty: number): string {
        return CartUtil.getItemsTotalQuantityTag(qty);
    }
}
